import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useSelector } from "react-redux";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { MapComponent } from "./MapComponent";
import api from "../../../../apiManager/api";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { MapPin, ArrowRight } from "lucide-react";
import { StepFooterNav } from "../../shared/NavigationFooter";

const SERVICES = [
  {
    name: "Snow Shoveling",
    img: "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/snow-shovel-2.svg",
    alt: "Snow Shoveling",
  },
  {
    name: "Leaf Raking",
    img: "https://i.imgur.com/3OSMtre.png",
    alt: "Leaf Raking",
  },
  {
    name: "Lawn Mowing",
    img: "https://shovelhousefrontendfmages.s3.ca-central-1.amazonaws.com/mowing.svg",
    alt: "Lawn Mowing",
  },
];

export default function Step1({ nextStep, preStep, currentStep, totalSteps }) {
  const { userInfo } = useSelector((state) => state.auth);
  const { jobPostProgress, setSelectedService } = useJobPostProgressContext();
  const [error, setError] = useState(false);
  const [isInLocation, setIsInLocation] = useState(false); // default false
  const [helperLocations, setHelperLocations] = useState([]);
  const [address] = useState(userInfo?.address || "");

  useEffect(() => {
    async function getHelperLocations() {
      try {
        const res = await api.get("/job/getHelpersEstimatedLocations");
        setHelperLocations(res.data.estimatedLocations ?? []);
      } catch (err) {
        console.error("Error fetching helpers:", err);
      }
    }
    getHelperLocations();
  }, []);

  const handleAddService = (service) => {
    setSelectedService(
      {
        service,
        serviceEquipment: [],
        serviceInstruction: "",
      },
      true
    );
    setError(false);
  };

  const handleNext = () => {
    if (!jobPostProgress.selectedService.service) {
      setError(true);
      return;
    }
    if (!isInLocation) {
      return; // silently block if location isn’t set
    }
    nextStep();
  };

  return (
    <div className="min-h-screen py-6 lg:py-12">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-2xl sm:text-4xl font-bold">Address & Service</h1>
          {/* Legend */}
          <div className="mx-auto mt-4 flex justify-between items-center w-full max-w-sm bg-primary/5 rounded-lg p-4 py-2 ring-2 ring-primary/30">
            <div className="relative px-4 w-[33%]">
              <img
                src="https://i.imgur.com/Wi4M3SK.png"
                alt=""
                className="w-20 h-20 object-contain"
              />
              <img
                src="https://i.imgur.com/FvPREE2.png"
                alt=""
                className="absolute -right-4 bottom-8"
              />
            </div>
            <p className="text-start w-[66%] pl-4 text-sm lg:text-base text-cyan-800">
              Look for the red circles to see where the helpers are in the
              neighbourhood
            </p>
          </div>
        </div>

        {/* Address + Map */}
        <Card className="pt-6">
          <CardContent className="space-y-6">
            <Label className="flex items-center gap-2 text-xl font-semibold">
              <MapPin className="w-6 h-6" /> Address for Service
            </Label>

            {/* Map */}
            <div className="h-80 lg:h-[26rem] rounded-xl overflow-hidden">
              <MapComponent
                {...{ setIsInLocation, isInLocation, helperLocations }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-center">Select a Service</h2>
          {error && (
            <p className="text-red-500 text-sm text-center">
              Please select one service.
            </p>
          )}
          <div className="grid grid-cols-3 gap-4">
            {SERVICES.map((service) => (
              <motion.div
                key={service.name}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`cursor-pointer rounded-lg transition ${jobPostProgress.selectedService.service === service.name
                    ? "ring-2 ring-primary bg-primary/10 shadow-md"
                    : "ring-2 ring-gray-100 hover:shadow-sm"
                  }`}
                onClick={() => handleAddService(service.name)}
              >
                <Card className="p-4 flex flex-col items-center justify-center text-center bg-transparent border-none">
                  <img
                    src={service.img}
                    alt={service.alt}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                  />
                  <CardContent className="p-2 font-medium">
                    {service.name}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer Navigation */}
        <StepFooterNav
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={preStep}
          onNext={handleNext}
          nextDisabled={
            !jobPostProgress.selectedService.service || !isInLocation
          }
        />
      </div>
    </div>
  );
}
